/**
 * What is not normal out there.
 *
 * The member-facing half of migration 0115. Deliberately a small,
 * unexcited panel and not a banner: a temporary green is a thing to know
 * before you go out, not an emergency, and the one loud element in this
 * product is reserved for lightning (CourseAlert.tsx). A module where
 * everything shouts is a module where nothing does.
 *
 * The one thing it does insist on is the count. "Seventeen holes today"
 * is the sentence a starter says forty times on a bad week, and it is
 * the sentence that decides whether a member should be paying a full
 * green fee — so it sits at the top in its own line rather than being
 * inferred from a list.
 */

import { useApi } from '../lib/useApi.ts';
import { Card, Empty, Status } from './ui.tsx';
import { date as fmtDate } from '../lib/format.ts';

export interface ConditionRow {
  condition_id: string;
  course_id: string;
  course_name: string;
  hole_id: string | null;
  playing_number: number | null;
  element: string;
  kind: string;
  note: string;
  starts_on: string;
  ends_on: string | null;
  days_open: number;
  until_further_notice: boolean;
  raised_by_name: string | null;
}

export interface CourseWholeness {
  holes_on_the_course: number;
  holes_out: number;
  holes_in_play: number;
  is_whole: boolean;
  what_is_out: string | null;
}

export interface ConditionsPayload {
  conditions: ConditionRow[];
  course: CourseWholeness | null;
}

/** The greenkeeper's word, not the enum. */
export const CONDITION_LABEL: Record<string, string> = {
  temporary_green: 'Temporary green',
  temporary_tee: 'Temporary tee',
  ground_under_repair: 'Ground under repair',
  out_of_play: 'Out of play',
  cart_path_only: 'Carts on the path',
  preferred_lies: 'Preferred lies',
  closed: 'Closed',
  other: 'Note',
};

export const ELEMENT_LABEL: Record<string, string> = {
  green: 'green',
  tee: 'tee',
  fairway: 'fairway',
  bunker: 'bunker',
  rough: 'rough',
  cart_path: 'cart path',
  practice: 'practice ground',
  general: '',
};

/** "The 12th green", "The 3rd", "Course-wide". */
export function conditionWhere(c: ConditionRow): string {
  const el = ELEMENT_LABEL[c.element] ?? c.element;
  if (c.playing_number === null) {
    return el ? `Every hole — ${el}` : 'Course-wide';
  }
  const n = c.playing_number;
  const suffix = n % 10 === 1 && n !== 11 ? 'st'
    : n % 10 === 2 && n !== 12 ? 'nd'
      : n % 10 === 3 && n !== 13 ? 'rd' : 'th';
  return el ? `${n}${suffix} ${el}` : `${n}${suffix}`;
}

/**
 * `key` is for the shop's copy of this panel.
 *
 * On the member pages nothing changes under them and a plain fetch is
 * right. On the admin screen the same panel sits directly above the form
 * that raises notices, and without a reload key it went on saying
 * "nothing out of the ordinary" immediately after somebody closed a
 * hole — which is the one moment they are looking at it to check their
 * own wording. Caught in a browser.
 */
export function useCourseConditions(
  courseId?: string | null, on?: string, key = 0,
) {
  const q = new URLSearchParams();
  if (courseId) q.set('course_id', courseId);
  if (on) q.set('on', on);
  const query = q.toString();
  return useApi<ConditionsPayload>(
    `/v1/golf/conditions${query ? `?${query}` : ''}`,
    [courseId ?? '', on ?? '', key],
  );
}

export function CourseConditions({
  courseId, on, title = 'The course today', reloadKey = 0,
}: {
  courseId?: string | null; on?: string; title?: string; reloadKey?: number;
}) {
  const data = useCourseConditions(courseId, on, reloadKey);
  const d = data.data;

  if (data.loading || !d) return null;

  const short = d.course && !d.course.is_whole;

  return (
    <Card title={title}>
      {/* The count first, and only when it is not eighteen. A line that
          says "18 of 18 holes" every day is a line people stop reading,
          and then do not read it on the day it says 17. */}
      {short && d.course && (
        <p className="condition-count">
          <strong>
            {d.course.holes_in_play} of {d.course.holes_on_the_course} holes today.
          </strong>{' '}
          {d.course.holes_out === 1 ? 'Hole' : 'Holes'} {d.course.what_is_out}{' '}
          {d.course.holes_out === 1 ? 'is' : 'are'} out of play.
        </p>
      )}

      {d.conditions.length === 0 ? (
        <Empty>
          Nothing out of the ordinary. The course is playing as it is printed on the
          card.
        </Empty>
      ) : (
        <ul className="conditions">
          {d.conditions.map((c) => (
            <li key={c.condition_id}>
              <div className="conditions__head">
                <span className="conditions__where">{conditionWhere(c)}</span>
                <Status tone={c.kind === 'closed' || c.kind === 'out_of_play'
                  ? 'warning' : 'neutral'}>
                  {CONDITION_LABEL[c.kind] ?? c.kind}
                </Status>
              </div>
              <div className="conditions__note">{c.note}</div>
              <div className="conditions__meta">
                {/* How long it has been like this. A green that has been
                    temporary since April is a different conversation
                    from one that went out yesterday, and only the date
                    tells anybody which they are having. */}
                since {fmtDate(c.starts_on)}
                {c.until_further_notice
                  ? ' · until further notice'
                  : c.ends_on ? ` · until ${fmtDate(c.ends_on)}` : ''}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
